/** A panel over a trigger, escaping whatever clips its cell.
 *
 *  ## Why a portal
 *
 *  The entry grid's popovers were `absolute` inside their `<td>`, which works only
 *  while nothing between the cell and the viewport clips. It never quite did: the
 *  wrapper around the table is `overflow-x: auto`, and per the CSS overflow rules an
 *  `auto` on one axis with `visible` on the other computes to `auto` on BOTH - so the
 *  wrapper has always been a vertical scroll container, and a picker opened on the
 *  last row was already being clipped or pushed into a scrollbar.
 *
 *  Bounding that box to `max-h-[60vh]` for the sticky header turns the latent problem
 *  into a visible one on any long list. So the panel is rendered into `document.body`
 *  and positioned as `fixed` against the trigger's own rectangle: nothing between the
 *  two can clip it any more.
 *
 *  ## Why `fixed` rather than measured page coordinates
 *
 *  `getBoundingClientRect` is already viewport-relative, which is exactly the
 *  coordinate space `position: fixed` uses - so there is no scroll offset to add, and
 *  nothing to get wrong when the page and the inner box scroll by different amounts.
 *  The cost is that the panel does not travel with its trigger, so it is re-measured
 *  on scroll and resize.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'

/** How far the panel keeps from the viewport edge, so it never sits flush. */
const MARGIN = 8

/** What Tab can land on inside the panel.
 *
 *  `:not([disabled])` is load-bearing rather than tidy: `CompanyPicker`'s "All active
 *  companies" checkbox is disabled whenever the filter matches nothing, and a
 *  selector that counted it would decide the panel's last focusable was an element
 *  the browser skips - so forward Tab would swallow the key and go nowhere.
 */
const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),'
  + 'select:not([disabled]),textarea:not([disabled]),'
  + '[tabindex]:not([tabindex="-1"])'

/** The focusables actually on screen, in tab order. */
function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    // `offsetParent` is null for anything `display:none`, which is how a collapsed
    // section hides. The `fixed` escape hatch is because a fixed element has no
    // offsetParent even when it is perfectly visible.
    (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed')
}

export function Popover({ anchor, open, onClose, onExit, children, align = 'left',
                          width }: {
  /** The trigger. Measured, never moved. */
  anchor: HTMLElement | null
  open: boolean
  onClose: () => void
  /** Tab (or Shift+Tab) has run off the end of the panel.
   *
   *  Without this the panel is a keyboard dead end, and a bad one. It is portalled to
   *  the end of `<body>`, so forward Tab off its last control had nowhere to go but
   *  the browser chrome - with the panel still open behind you - and Shift+Tab off
   *  its first control landed on the tail of the whole app, jumping from row three to
   *  the bottom of the page.
   *
   *  Deliberately NOT `onClose`: the caller decides how to close, which matters
   *  because the Company cell must close through `applyPick` - the ticks are the
   *  decision, and a Tab-out that discarded them would contradict clicking away.
   */
  onExit?: (dir: 'forward' | 'back') => void
  children: ReactNode
  /** Which edge of the panel lines up with the trigger. The Documents cell is near
   *  the right of a wide table, where a left-aligned panel runs off the screen. */
  align?: 'left' | 'right'
  /** The panel's width. Defaults to the pickers' 320px, capped to the viewport. */
  width?: number
}) {
  const panel = useRef<HTMLDivElement>(null)
  const [box, setBox] = useState<{ top: number; left: number } | null>(null)

  const place = useCallback(() => {
    const el = anchor
    const p = panel.current
    if (!el || !p) return
    const t = el.getBoundingClientRect()
    const h = p.offsetHeight
    const w = p.offsetWidth

    // Below the trigger, unless there is no room below and more room above - which
    // is the case for the last rows of a long grid, and the whole point of this.
    const below = t.bottom + 4
    const above = t.top - h - 4
    const room = window.innerHeight - t.bottom - MARGIN
    const top = room >= h || above < MARGIN ? below : above

    let left = align === 'right' ? t.right - w : t.left
    // Never off either edge, whichever way it was aligned.
    left = Math.min(left, window.innerWidth - w - MARGIN)
    left = Math.max(MARGIN, left)
    setBox({ top, left })
  }, [anchor, align])

  // Layout effect, so the panel is placed before it is painted. Measured after it
  // mounts, because the height is not known until its content is laid out - which is
  // why `box` starts null and the panel is hidden for that first pass.
  useLayoutEffect(() => {
    if (!open) { setBox(null); return }
    place()
  }, [open, place, children])

  useEffect(() => {
    if (!open) return
    // Capture, so scrolling the grid's own box counts and not just the page.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  if (!open) return null

  return createPortal(
    <>
      {/* The scrim, which is what closing by clicking away goes through. Hidden from
          assistive tech: it is decorative, and it is the only reason keyboard
          dismissal ever looked like something this component provided. */}
      <div aria-hidden="true" className="fixed inset-0 z-[60]" onClick={onClose} />
      <div ref={panel} data-popover
           onKeyDown={(e) => {
             if (!onExit) return
             const root = panel.current
             if (!root) return
             // Escape from anywhere in the panel, not just the search box. The
             // pickers wire it on their input, so from the Done button or a quorum
             // radio it did nothing - and they `preventDefault` when they handle it,
             // so this cannot fire twice.
             if (e.key === 'Escape' && !e.defaultPrevented) {
               e.preventDefault()
               e.stopPropagation()
               onExit('back')
               return
             }
             if (e.key !== 'Tab') return
             const stops = focusables(root)
             const at = document.activeElement as HTMLElement | null
             const i = at ? stops.indexOf(at) : -1
             // Nothing focusable, or focus on the panel itself: either direction is
             // an exit rather than a swallowed keypress.
             const leaving = i === -1
               ? true
               : e.shiftKey ? i === 0 : i === stops.length - 1
             if (!leaving) return
             // `preventDefault` before the browser moves anything, so focus never
             // flickers into the address bar on its way out.
             e.preventDefault()
             e.stopPropagation()
             onExit(e.shiftKey ? 'back' : 'forward')
           }}
           style={{
             top: box?.top ?? 0, left: box?.left ?? 0,
             width: width ? `min(${width}px, calc(100vw - 2rem))`
                          : 'min(320px, calc(100vw - 2rem))',
             // Transparent, NOT `visibility: hidden`, for the measuring pass. It has
             // to be in the document to have a height, and painted at the wrong
             // place for a frame would read as a flicker - but a
             // `visibility: hidden` subtree cannot take focus, so the picker's
             // autofocused search box silently lost it and every key went to the
             // page instead. `opacity` hides it just as well and stays focusable.
             //
             // There is no flash: the position is set in a layout effect, which
             // re-renders before the browser paints.
             opacity: box ? 1 : 0,
             pointerEvents: box ? undefined : 'none',
           }}
           className="fixed z-[61] bg-surface border border-rule rounded-lg
                      shadow-2xl">
        {children}
      </div>
    </>,
    document.body)
}
